import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import DictionaryReducer from '../util/DictionaryReducer'

export interface ErrorItem {
  message: string
  isVisible: boolean
}
export type ErrorsState = Record<string, ErrorItem>
export interface UseErrorValue {
  errors: ErrorsState
  addError: (message: string) => void
  removeError: (id: string) => void
}
export type ErrorContextValue = ReturnType<typeof useError>
export type ErrorProviderProps = PropsWithChildren<ErrorContextValue>

const HIDE_ANIMATION_MS = 1000
const AUTO_HIDE_MS = 20000
const ErrorContext = createContext<ErrorContextValue | null>(null)

export const ErrorProvider = ({ children, ...value }: ErrorProviderProps): JSX.Element => {
  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
}

export const useError = (): UseErrorValue => {
  const [errors, dispatchErrors] = useReducer(DictionaryReducer, {} as ErrorsState)
  const timeoutIdsRef = useRef<Array<ReturnType<typeof setTimeout>>>([])

  const trackTimeout = useCallback((id: ReturnType<typeof setTimeout>) => {
    timeoutIdsRef.current.push(id)
  }, [])

  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach(clearTimeout)
      timeoutIdsRef.current = []
    }
  }, [])

  const removeError = useCallback((id: string) => {
    dispatchErrors({ type: 'remove', key: id })
  }, [])

  const hideError = useCallback(
    (id: string) => {
      dispatchErrors({
        type: 'update',
        key: id,
        value: { isVisible: false },
      })
      trackTimeout(setTimeout(() => removeError(id), HIDE_ANIMATION_MS))
    },
    [removeError, trackTimeout]
  )

  const addError = useCallback(
    (message: string) => {
      const id = uuidv4()
      dispatchErrors({
        type: 'add',
        key: id,
        value: { message, isVisible: true },
      })
      trackTimeout(setTimeout(() => hideError(id), AUTO_HIDE_MS))
    },
    [hideError, trackTimeout]
  )

  return {
    errors,
    addError,
    removeError,
  }
}

export const useErrorHandling = (): ErrorContextValue => {
  const context = useContext(ErrorContext)
  if (!context) {
    throw new Error('useErrorHandling must be used within an ErrorProvider')
  }
  return context
}
